export interface StepLike {
  id: string;
  stepNumber: number;
  title?: string | null;
  pageUrl?: string | null;
  surface?: string | null;
  routeKey?: string | null;
}

export interface Milestone {
  id: string;
  key: string;
  label: string;
  stepNumbers: number[];
  stepIds: string[];
  startStepNumber: number;
  endStepNumber: number;
  stepCount: number;
  isRevisit: boolean;
}

const UUID_RE =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const LONG_NUM_RE = /(?<=\/)\d{5,}(?=\/|$|\?|#)/g;
const PATH_NUM_RE = /\/\d+(?=\/|$|\?|#)/g;

/** Replaces volatile UUIDs and numeric IDs in paths with placeholders */
export function templatePath(path: string): string {
  return path
    .replace(UUID_RE, ":id")
    .replace(LONG_NUM_RE, ":id")
    .replace(PATH_NUM_RE, "/:n");
}

/** Extracts the pathname from an absolute or relative URL string */
export function pathOfUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    return u.pathname || "/";
  } catch {
    const clean = url.split("?")[0].split("#")[0].trim();
    if (!clean) return null;
    return clean.startsWith("/") ? clean : `/${clean}`;
  }
}

/**
 * Extracts a stable surface key and user-facing label from step metadata.
 */
export function extractStepSurface(step: StepLike): {
  key: string;
  label: string;
} {
  // 1. Explicit surface recorded by walker / explore signature
  if (step.surface && step.surface.trim()) {
    const label = step.surface.trim();
    return { key: label.toLowerCase(), label };
  }

  // 2. Pre-computed routeKey (e.g. from route patterns)
  if (step.routeKey && step.routeKey.trim()) {
    const label = step.routeKey.trim();
    return { key: label.toLowerCase(), label };
  }

  // 3. Normalized path from pageUrl
  if (step.pageUrl && step.pageUrl.trim()) {
    const rawPath = pathOfUrl(step.pageUrl);
    if (rawPath) {
      const templated = templatePath(rawPath);
      return { key: templated.toLowerCase(), label: templated };
    }
  }

  // 4. Action-based title inference fallback
  if (step.title) {
    const match = step.title.match(
      /(?:on|at|navigated to|visited)\s+([A-Za-z0-9_\-/\s]+)/i,
    );
    if (match && match[1]?.trim()) {
      const label = match[1].trim();
      return { key: label.toLowerCase(), label };
    }
  }

  return { key: "root", label: "Initial screen" };
}

/**
 * Groups a sequence of steps into chronological milestones by surface identity.
 * Flags revisits when a surface is returned to after navigating away.
 */
export function buildMilestones(steps: readonly StepLike[]): Milestone[] {
  if (!steps || steps.length === 0) return [];

  const milestones: Milestone[] = [];
  const seenKeys = new Set<string>();
  let current: Milestone | null = null;

  for (const step of steps) {
    const { key, label } = extractStepSurface(step);

    if (!current || current.key !== key) {
      const isRevisit = seenKeys.has(key);
      seenKeys.add(key);

      current = {
        id: `milestone-${milestones.length}`,
        key,
        label,
        stepNumbers: [step.stepNumber],
        stepIds: [step.id],
        startStepNumber: step.stepNumber,
        endStepNumber: step.stepNumber,
        stepCount: 1,
        isRevisit,
      };
      milestones.push(current);
    } else {
      current.stepNumbers.push(step.stepNumber);
      current.stepIds.push(step.id);
      current.endStepNumber = step.stepNumber;
      current.stepCount++;
    }
  }

  return milestones;
}
